import React, { ReactElement, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { useForm } from '@tanstack/react-form'
import { z } from 'zod'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectPlanningSections,
  selectPlanningTemplates,
  selectPlanningCourseV2CoreSaveStatusByCourseID,
  selectPlanningCourseV2SyllabusSaveStatusByCourseID,
  updatePlanningCourseV2Core,
  updatePlanningCourseV2Syllabus,
  fetchPlanningCourseV2Details,
  fetchCourseV2Compliances,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import {
  ContentComplianceStatus,
  PlanningCourseV2Details,
} from 'src/v3/core/types'
import ImageUpload from 'src/v3/components/upload-image'
import axios from 'src/utilities/http-client'
import { updateCourseV2Image } from 'src/v3/content-pipeline/utils/course-api'
import {
  downloadOriginalImage,
  getS3ImageFilePath,
  getS3OriginalImageFilePath,
} from 'src/v3/utilities/image-upload'
import SaveConfirmation from '../components/save-confirmation'

// Fields that belong to the core PATCH endpoint
let CORE_FIELDS = ['name', 'sectionID', 'templateID', 'length'] as const
type CoreField = (typeof CORE_FIELDS)[number]

// Fields that belong to the syllabus PUT endpoint
let SYLLABUS_FIELDS = [
  'description',
  'practiceGap',
  'practiceGapEvidence',
  'learnerEngagement',
  'learningOutcomes',
] as const
type SyllabusField = (typeof SYLLABUS_FIELDS)[number]

// Per-field validation — mirrors Create v5 coreInfoSchema / syllabusSchema
let fieldValidators: Partial<Record<CourseFieldName, z.ZodTypeAny>> = {
  name: z
    .string()
    .min(1, 'Course name is required')
    .max(200, 'Course name must be less than 200 characters'),
  length: z.coerce.number().positive('CE hours must be a positive number'),
  sectionID: z.string().min(1, 'Category is required'),
  templateID: z.string().min(1, 'Template is required'),
}

type ImageField = 'thumbnail' | 'banner'

export type CourseFieldName = CoreField | SyllabusField | ImageField

let statusColors: Record<
  ContentComplianceStatus,
  'primary' | 'success' | 'error'
> = {
  new: 'primary',
  approved: 'success',
  rejected: 'error',
  updated: 'primary',
  unapproved: 'error',
}

let statusLabels: Record<ContentComplianceStatus, string> = {
  new: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  updated: 'Updated – Pending Review',
  unapproved: 'Unapproved',
}

type CourseFieldProps = {
  courseID: string
  course: PlanningCourseV2Details
  fieldName: CourseFieldName
  fieldLabel: string
  fieldType: 'text' | 'textarea' | 'number' | 'select' | 'image'
  complianceStatus?: ContentComplianceStatus
  complianceFeedback?: string
  required?: boolean
  onSave?: () => void
}

export default function CourseField({
  courseID,
  course,
  fieldName,
  fieldLabel,
  fieldType,
  complianceStatus,
  complianceFeedback,
  required = false,
  onSave,
}: CourseFieldProps): ReactElement {
  let dispatch = useAppDispatch()
  let sections = useAppSelector(selectPlanningSections)
  let templates = useAppSelector(selectPlanningTemplates)
  let coreSaveStatus = useAppSelector(
    selectPlanningCourseV2CoreSaveStatusByCourseID(courseID),
  )
  let syllabusSaveStatus = useAppSelector(
    selectPlanningCourseV2SyllabusSaveStatusByCourseID(courseID),
  )
  let [isEditing, setIsEditing] = useState(false)
  let [submitted, setSubmitted] = useState(false)
  let [imageLoading, setImageLoading] = useState(false)
  let [imageFile, setImageFile] = useState<File | undefined>(undefined)
  let [confirmOpen, setConfirmOpen] = useState(false)
  let [validationError, setValidationError] = useState<string | null>(null)
  let [downloadingImage, setDownloadingImage] = useState(false)
  let [displayImgError, setDisplayImgError] = useState(false)
  let [displayIsRetrying, setDisplayIsRetrying] = useState(false)
  let [displayRetryCount, setDisplayRetryCount] = useState(0)
  let [savedAt, setSavedAt] = useState<number | null>(null)
  let displayRetryTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  let DISPLAY_MAX_RETRIES = 3

  let isCore = (CORE_FIELDS as readonly string[]).includes(fieldName)
  let saveStatus = isCore ? coreSaveStatus : syllabusSaveStatus
  let isSaving =
    fieldType === 'image' ? imageLoading : submitted && saveStatus === 'pending'
  let saveFailed =
    fieldType !== 'image' && submitted && saveStatus === 'rejected'
  let isApproved = complianceStatus === 'approved'
  let currentValue = (course as any)[fieldName] ?? ''

  let form = useForm({
    defaultValues: { value: currentValue as string | number },
    onSubmit: async ({ value }) => {
      // Validate before saving
      let validator = fieldValidators[fieldName]
      if (validator) {
        let result = validator.safeParse(value.value)
        if (!result.success) {
          setValidationError(result.error.issues[0]?.message ?? 'Invalid value')
          return
        }
      }
      setValidationError(null)

      if (fieldType === 'image' && imageFile) {
        setImageLoading(true)
        try {
          let signedUrl = await updateCourseV2Image({
            id: courseID,
            type: fieldName === 'thumbnail' ? 'Thumbnail' : 'Banner',
            contentType: imageFile.type,
          })
          await axios.put(signedUrl, imageFile, {
            headers: {
              'Cache-Control': 'max-age=2592000',
              'content-type': imageFile.type,
            },
          })
          setImageFile(undefined)
          setIsEditing(false)
          setSavedAt(Date.now())
          dispatch(fetchPlanningCourseV2Details(courseID))
          dispatch(fetchCourseV2Compliances())
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to upload image:', error)
          return
        } finally {
          setImageLoading(false)
        }
      } else if ((CORE_FIELDS as readonly string[]).includes(fieldName)) {
        setSubmitted(true)
        dispatch(
          updatePlanningCourseV2Core({
            courseId: courseID,
            payload: {
              name: fieldName === 'name' ? String(value.value) : course.name,
              sectionID:
                fieldName === 'sectionID'
                  ? String(value.value)
                  : course.sectionID,
              templateID:
                fieldName === 'templateID'
                  ? String(value.value)
                  : course.templateID,
              length:
                fieldName === 'length' ? Number(value.value) : course.length,
            },
          }),
        )
      } else if ((SYLLABUS_FIELDS as readonly string[]).includes(fieldName)) {
        setSubmitted(true)
        dispatch(
          updatePlanningCourseV2Syllabus({
            courseId: courseID,
            payload: { [fieldName]: String(value.value) || null },
          }),
        )
      }
    },
  })

  useEffect(() => {
    if (displayRetryTimeoutRef.current) {
      clearTimeout(displayRetryTimeoutRef.current)
    }
    setDisplayImgError(false)
    setDisplayIsRetrying(false)
    setDisplayRetryCount(0)
  }, [currentValue])

  useEffect(
    () => () => {
      if (displayRetryTimeoutRef.current) {
        clearTimeout(displayRetryTimeoutRef.current)
      }
    },
    [],
  )

  useEffect(() => {
    if (submitted && saveStatus === 'fulfilled') {
      // The write emits a compliance event server-side; refetch so the
      // badge reflects it (e.g. approved → updated)
      dispatch(fetchCourseV2Compliances())
      setIsEditing(false)
      setSubmitted(false)
      setValidationError(null)
      setSavedAt(Date.now())
      form.reset()
      onSave?.()
    }
  }, [saveStatus, submitted])

  let handleDownloadOriginal = async () => {
    setDownloadingImage(true)
    try {
      let key = String(currentValue)
      let extension = key.match(/\.([^./]+)$/)?.[1] || 'jpg'
      await downloadOriginalImage(
        getS3OriginalImageFilePath(key),
        `${fieldName}-${courseID}.${extension}`,
      )
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to download original image:', error)
    } finally {
      setDownloadingImage(false)
    }
  }

  let openEdit = () => {
    form.setFieldValue('value', currentValue)
    setValidationError(null)
    setSavedAt(null)
    setIsEditing(true)
  }

  let renderDisplay = () => {
    if (fieldType === 'image') {
      return (
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paperDark',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {currentValue ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {displayImgError || displayIsRetrying ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    height: 80,
                    width: 160,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="caption" color="text.secondary">
                    {displayImgError ? 'Image unavailable' : 'Processing…'}
                  </Typography>
                </Box>
              ) : (
                <img
                  style={{
                    maxHeight: 80,
                    maxWidth: 160,
                    aspectRatio: fieldName === 'banner' ? '4 / 1' : '16 / 9',
                    borderRadius: 4,
                    objectFit: 'cover',
                  }}
                  src={(() => {
                    let base = getS3ImageFilePath(String(currentValue))
                    return displayRetryCount > 0
                      ? `${base}?retry=${displayRetryCount}`
                      : base
                  })()}
                  alt={fieldLabel}
                  onError={() => {
                    setDisplayIsRetrying(true)
                    if (displayRetryCount < DISPLAY_MAX_RETRIES) {
                      // eslint-disable-next-line fp/no-mutation
                      displayRetryTimeoutRef.current = setTimeout(
                        () => {
                          setDisplayIsRetrying(false)
                          setDisplayRetryCount((c) => c + 1)
                        },
                        2000 * 2 ** displayRetryCount,
                      )
                    } else {
                      setDisplayIsRetrying(false)
                      setDisplayImgError(true)
                    }
                  }}
                />
              )}
              {fieldName === 'thumbnail' &&
                !String(currentValue).includes('originals') && (
                  <Button
                    component="a"
                    href={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${String(currentValue)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="text"
                    sx={{ p: 0, justifyContent: 'flex-start', fontSize: 11 }}
                  >
                    View original thumbnail
                  </Button>
                )}
            </Box>
          ) : (
            <Typography
              color="text.secondary"
              component="span"
              sx={{
                fontStyle: 'italic',
              }}
            >
              No image uploaded
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {currentValue && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownloadOriginal}
                loading={downloadingImage}
                disabled={downloadingImage}
                aria-label={`Download original ${fieldLabel}`}
              >
                Download Original
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSavedAt(null)
                if (isApproved) {
                  setConfirmOpen(true)
                } else {
                  setIsEditing(true)
                }
              }}
              aria-label={`Upload ${fieldLabel}`}
            >
              Upload
            </Button>
          </Box>
        </Box>
      )
    }

    let displayValue = (() => {
      if (fieldName === 'sectionID') {
        return sections.find((s) => s.id === currentValue)?.name ?? ''
      }
      if (fieldName === 'templateID') {
        return templates.find((t) => t.id === currentValue)?.name ?? ''
      }
      return String(currentValue)
    })()

    return (
      <Box sx={{ position: 'relative' }}>
        <TextField
          sx={{ bgcolor: 'background.paperDark' }}
          fullWidth
          multiline={fieldType === 'textarea'}
          rows={fieldType === 'textarea' ? 3 : undefined}
          value={displayValue}
          placeholder="Not set"
          disabled
        />
        <Button
          variant="text"
          size="small"
          onClick={() => (isApproved ? setConfirmOpen(true) : openEdit())}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            minWidth: 'auto',
            px: 1,
          }}
          aria-label={`Edit ${fieldLabel}`}
        >
          Edit
        </Button>
      </Box>
    )
  }

  let renderEdit = () => {
    if (fieldType === 'image') {
      // v1 images live under /public/<name>; v2 (Create v5 / Content
      // Pipeline) images live under an originals/ path resized to /images/.
      // ImageUpload needs to know which base path to hit, same as Create v5's
      // getImageBasePath — guessing wrong 404s immediately and shows the
      // "Processing image…" retry state even though the image is fine.
      let imageBasePath: 'images' | 'public' = String(currentValue).includes(
        'originals',
      )
        ? 'images'
        : 'public'
      return (
        <ImageUpload
          id={`course-field-${fieldName}`}
          type={fieldName === 'thumbnail' ? 'thumbnail' : 'banner'}
          image={imageFile}
          setImage={setImageFile}
          existingImage={String(currentValue)}
          imageBasePath={imageBasePath}
        />
      )
    }

    if (fieldType === 'select') {
      let options = fieldName === 'sectionID' ? sections : templates
      return (
        <form.Field
          name="value"
          children={(field) => (
            <FormControl fullWidth>
              <Select
                sx={{ bgcolor: 'background.paperDark' }}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={isSaving}
              >
                <MenuItem value="">Unselected</MenuItem>
                {options.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )
    }

    if (fieldType === 'number') {
      return (
        <form.Field
          name="value"
          children={(field) => (
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              type="number"
              value={field.state.value}
              onChange={(e) => field.handleChange(Number(e.target.value))}
              onBlur={field.handleBlur}
              disabled={isSaving}
              slotProps={{
                htmlInput: { min: 0, step: 0.5 },
              }}
            />
          )}
        />
      )
    }

    return (
      <form.Field
        name="value"
        children={(field) => (
          <TextField
            sx={{ bgcolor: 'background.paperDark' }}
            fullWidth
            multiline={fieldType === 'textarea'}
            rows={fieldType === 'textarea' ? 3 : undefined}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            disabled={isSaving}
          />
        )}
      />
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
          }}
        >
          {fieldLabel}
          {required && (
            <Typography color="error" component="span" aria-hidden>
              {' '}
              *
            </Typography>
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <SaveConfirmation savedAt={savedAt} />
          {isApproved && (
            <LockIcon
              fontSize="small"
              sx={{
                color: 'success.main',
              }}
            />
          )}
          {complianceStatus && (
            <Chip
              size="small"
              label={statusLabels[complianceStatus]}
              variant="outlined"
              color={statusColors[complianceStatus]}
            />
          )}
        </Box>
      </Box>
      {!isEditing ? (
        renderDisplay()
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>{renderEdit()}</Box>
          {validationError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {validationError}
            </Alert>
          )}
          {saveFailed && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Save failed. Please try again.
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => form.handleSubmit()}
              loading={isSaving}
              disabled={isSaving || (fieldType === 'image' && !imageFile)}
              aria-label={`Save ${fieldLabel}`}
            >
              {fieldType === 'image' ? 'Save Image' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsEditing(false)
                setValidationError(null)
                setImageFile(undefined)
                form.reset()
              }}
              disabled={isSaving}
              aria-label={`Cancel editing ${fieldLabel}`}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
      {complianceFeedback &&
        (complianceStatus === 'rejected' ||
          complianceStatus === 'unapproved') && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
              }}
            >
              Compliance Feedback
            </Typography>
            <Typography variant="body2">{complianceFeedback}</Typography>
          </Alert>
        )}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        aria-labelledby="course-field-confirm-title"
        aria-describedby="course-field-confirm-description"
        maxWidth="xs"
      >
        <DialogTitle id="course-field-confirm-title">
          Edit Approved Field
        </DialogTitle>
        <DialogContent id="course-field-confirm-description">
          <Typography variant="body2">
            This field has been approved. Editing will require re-approval.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false)
              openEdit()
            }}
          >
            Edit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
